// ZUSTAND→ATOM off-ramp (docs/projects/complete/conversion/effect-native-doc-pack.md)
// Status: converted
// Active-doc marker is a keepAlive atom. (Page/blob data stays on
// effect-query; an Atom.family layer remains a possible future step.)
import type { DocPackInfo } from '@listening/api-contract/schemas';
import type { HttpClient } from 'effect/http';
import {
  DOC_PACK_CONTENT_VERSION,
  type DocPackCatalog,
  type DocPackCorruptError,
  type DocPackPage,
  type DocPackRangeFetchError,
  docPackPageByteRange,
} from '@listening/doc-pack';
import {
  decodeDocPackPageBlob,
  fetchDocPackByteRange,
  fetchDocPackPage as fetchDocPackPageFromPack,
  fetchDocPackPages,
  openDocPackCatalogFromUrl,
  type LoadedDocPack,
} from '@listening/doc-pack/effect';
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import type { QueryClient } from '@tanstack/react-query';
import { Effect } from 'effect';
import { Atom } from 'effect/reactivity';
import type { AppRuntime, Eq } from '../query/runtime-context';
import {
  buildDocPackReadingChunks,
  docPackCatalogPageReadableCounts,
  findDocPackChunkContaining,
  type DocPackReadingAnchor,
  type DocPackReadingChunk,
  type DocPackReadingClassOverrides,
} from './reading-plan';

const FULL_BLOB_LIMIT_BYTES = 4 * 1024 * 1024;
export const DOC_PACK_PAGE_CACHE_GC_TIME_MS = 120_000;
export type DocPackPageFetchMode = 'auto' | 'range';

// The ranged-read protocol (catalog-open ritual, range semantics, typed
// errors, timeout) lives in @listening/doc-pack/effect — shared with the
// backend readers. This file keeps only the query integration: eq-agnostic
// query args (wrap with `useEq().queryOptions(...)` in components, or the
// route-runtime `eq` in loaders), prefetch, and the small-pack full-blob
// cache. Failures surface as EffectQueryFailure carrying the typed doc-pack
// errors on `.failure`.
export type { LoadedDocPack } from '@listening/doc-pack/effect';
export {
  DocPackCorruptError,
  DocPackPageAbsentError,
  DocPackRangeFetchError,
} from '@listening/doc-pack';

type DocPackReadError = DocPackRangeFetchError | DocPackCorruptError;

// These effects require the HttpClient service — every app runtime provides
// FetchHttpClient.layer (see AppRuntime in ../query/runtime-context), so the
// requirement is satisfied at the eq/runtime boundary, not here.
export const fetchDocPackCatalogEffect = (
  url: string,
): Effect.Effect<LoadedDocPack, DocPackReadError, HttpClient.HttpClient> =>
  openDocPackCatalogFromUrl(url);

export const docPackCatalogArgs = (audioId: string, url: string) => ({
  queryKey: ['doc-pack-catalog', audioId, url] as const,
  queryFn: () => fetchDocPackCatalogEffect(url),
  staleTime: Number.POSITIVE_INFINITY,
  retry: 1,
});

export const docPackPageQueryKey = (
  audioId: string,
  packUrl: string,
  page: number,
) => ['doc-pack-page', audioId, packUrl, page] as const;

const EMPTY_PAGE = (page: number): DocPackPage => ({
  page,
  size: [612, 792],
  textSource: 'ocr',
  degraded: false,
  text: '',
  words: [],
  sentences: [],
  blocks: [],
  segments: [],
});

const totalBlobBytes = (catalog: DocPackCatalog) => {
  let max = 0;
  for (const page of catalog.pages) {
    if (page.offset + page.length > max) max = page.offset + page.length;
  }
  return max;
};

const fullBlobCache = new Map<
  string,
  Effect.Effect<Uint8Array, DocPackReadError, HttpClient.HttpClient>
>();

export function resetDocPackBlobCacheForTesting() {
  fullBlobCache.clear();
}

/**
 * Whole-blob download, memoized per pack URL. `Effect.cached` is the memo
 * cell: concurrent readers share one in-flight download, later readers get
 * the settled bytes, and a failure evicts the cell so the next reader
 * retries. The cell is opened lazily on the first reading fiber (which has
 * HttpClient in context — `Effect.cached` captures it there); the double
 * check after opening makes a concurrent first-read race adopt the winner's
 * cell, so the loser's never-executed handle downloads nothing.
 */
export function fullDocPackBlobRegionEffect(
  pack: LoadedDocPack,
): Effect.Effect<Uint8Array, DocPackReadError, HttpClient.HttpClient> {
  return Effect.suspend(() => {
    const cached = fullBlobCache.get(pack.url);
    if (cached) return cached;
    return Effect.cached(
      fetchDocPackByteRange(
        pack.url,
        pack.blobBaseOffset,
        pack.blobBaseOffset + totalBlobBytes(pack.catalog) - 1,
      ).pipe(
        Effect.onError(() => Effect.sync(() => fullBlobCache.delete(pack.url))),
      ),
    ).pipe(
      Effect.flatMap((region) => {
        const raced = fullBlobCache.get(pack.url);
        if (raced) return raced;
        fullBlobCache.set(pack.url, region);
        while (fullBlobCache.size > 2) {
          const oldest = fullBlobCache.keys().next().value;
          if (oldest === undefined) break;
          fullBlobCache.delete(oldest);
        }
        return region;
      }),
    );
  });
}

export function decodeDocPackPageFromRegionEffect(
  pack: LoadedDocPack,
  region: Uint8Array,
  page: number,
): Effect.Effect<DocPackPage, DocPackCorruptError> {
  const range = docPackPageByteRange(pack.catalog, pack.blobBaseOffset, page);
  if (!range) return Effect.succeed(EMPTY_PAGE(page));
  const blob = region.subarray(
    range.start - pack.blobBaseOffset,
    range.endInclusive + 1 - pack.blobBaseOffset,
  );
  return decodeDocPackPageBlob(pack.url, page, blob);
}

export function fetchDocPackPageEffect(
  pack: LoadedDocPack,
  page: number,
  options?: { fetchMode?: DocPackPageFetchMode },
): Effect.Effect<DocPackPage, DocPackReadError, HttpClient.HttpClient> {
  const range = docPackPageByteRange(pack.catalog, pack.blobBaseOffset, page);
  if (!range) return Effect.succeed(EMPTY_PAGE(page));
  if (
    options?.fetchMode !== 'range' &&
    totalBlobBytes(pack.catalog) <= FULL_BLOB_LIMIT_BYTES
  ) {
    return fullDocPackBlobRegionEffect(pack).pipe(
      Effect.flatMap((region) =>
        decodeDocPackPageFromRegionEffect(pack, region, page),
      ),
    );
  }
  return fetchDocPackPageFromPack(pack, page).pipe(
    // The range was just resolved from the catalog, so absence can't happen;
    // keep the channel to the two read errors regardless.
    Effect.catchTag('DocPackPageAbsentError', () =>
      Effect.succeed(EMPTY_PAGE(page)),
    ),
  );
}

export const docPackPageArgs = (
  audioId: string,
  pack: LoadedDocPack,
  page: number,
  options?: { fetchMode?: DocPackPageFetchMode },
) => ({
  queryKey: docPackPageQueryKey(audioId, pack.url, page),
  queryFn: () => fetchDocPackPageEffect(pack, page, options),
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: DOC_PACK_PAGE_CACHE_GC_TIME_MS,
  retry: 1,
});

export function prefetchDocPackPages(
  eq: Eq,
  queryClient: QueryClient,
  audioId: string,
  pack: LoadedDocPack,
  pages: ReadonlyArray<number>,
  options?: { fetchMode?: DocPackPageFetchMode },
) {
  for (const page of pages) {
    if (page < 1 || page > pack.catalog.pageCount) continue;
    void queryClient
      .query(eq.queryOptions(docPackPageArgs(audioId, pack, page, options)))
      .catch(() => undefined);
  }
}

/** Readable candidate pages warmed per coalesced batch during a chunk scan. */
const DOC_PACK_SCAN_BATCH_PAGES = 8;

/**
 * The subset of `pageNumbers` not already in the per-page react-query cache.
 * Warming must never re-fetch a page a prior load (or an earlier warm) already
 * seeded, so this is the cache consult that gates {@link warmDocPackPagesCoalesced}.
 */
export function selectDocPackPagesToWarm(
  queryClient: QueryClient,
  audioId: string,
  packUrl: string,
  pageNumbers: readonly number[],
): number[] {
  return pageNumbers.filter(
    (page) =>
      queryClient.getQueryData(docPackPageQueryKey(audioId, packUrl, page)) ===
      undefined,
  );
}

/**
 * Batch-fetch the not-yet-cached pages among `pageNumbers` in one coalesced
 * range request and seed the per-page react-query cache, so the scan's
 * subsequent per-page `ensureQueryData` calls hit the cache instead of each
 * issuing a round trip. Cached pages are skipped (see
 * {@link selectDocPackPagesToWarm}); a single uncached page isn't worth its own
 * request (the scan's per-page load fetches it). Best-effort: a failed warm is
 * swallowed and the real per-page load surfaces the typed error. Only
 * meaningful in `range` fetch mode — auto/full-blob mode already shares a
 * single memoized download.
 */
export async function warmDocPackPagesCoalesced(
  runtime: AppRuntime,
  queryClient: QueryClient,
  audioId: string,
  pack: LoadedDocPack,
  pageNumbers: readonly number[],
): Promise<void> {
  const toWarm = selectDocPackPagesToWarm(
    queryClient,
    audioId,
    pack.url,
    pageNumbers,
  );
  if (toWarm.length <= 1) return;
  const pages = await runtime
    .runPromise(fetchDocPackPages(pack, toWarm))
    .catch(() => undefined);
  if (!pages) return;
  for (const [page, decoded] of pages) {
    queryClient.setQueryData(
      docPackPageQueryKey(audioId, pack.url, page),
      decoded,
    );
  }
}

/**
 * Scan forward from `startPage` for the stable chunk to play at `anchor`,
 * consulting the catalog so pages with zero readable chars (under the active
 * class overrides) are SKIPPED without any fetch — a seek into a trailing
 * non-readable region then costs zero round trips instead of one per page.
 *
 * The scan blocks on exactly ONE page at a time — the candidate it needs to
 * inspect — via `loadPage` (the react-query cache seam). When that page yields
 * the chunk it returns immediately, never touching later pages. Only when a
 * page does NOT yield the chunk does it kick off `warmPages` for the next
 * window in the BACKGROUND (fire-and-forget), so a run of readable pages can
 * coalesce into one range request without this resolution ever blocking on
 * pages beyond the one it needs.
 *
 * Determinism: the returned chunk is identical to a naive page-by-page scan.
 * A zero-readable page can never yield a chunk — its catalog readable count is
 * 0 exactly when `buildDocPackReadingChunks` would emit nothing (both honor
 * the same class-enablement policy) — so skipping it changes only the fetch
 * pattern, never the result; and readable candidates are visited in ascending
 * page order, so the first match is the same page the naive scan returns.
 */
export async function findDocPackChunkByScan(params: {
  readonly catalog: DocPackCatalog;
  readonly classOverrides: DocPackReadingClassOverrides;
  readonly anchor: DocPackReadingAnchor;
  readonly startPage: number;
  readonly loadPage: (pageNumber: number) => Promise<DocPackPage>;
  readonly warmPages?: (pageNumbers: readonly number[]) => Promise<void>;
  readonly batchPages?: number;
}): Promise<DocPackReadingChunk | undefined> {
  const { catalog, classOverrides, anchor, startPage } = params;
  const batchPages = params.batchPages ?? DOC_PACK_SCAN_BATCH_PAGES;
  const candidates = catalog.pages
    .filter(
      (page) =>
        page.n >= startPage &&
        page.n <= catalog.pageCount &&
        docPackCatalogPageReadableCounts(page, classOverrides).chars > 0,
    )
    .map((page) => page.n)
    // Ascending page order matches the naive scan.
    .toSorted((a, b) => a - b);

  let warmedThrough = -1;
  for (let i = 0; i < candidates.length; i++) {
    const pageNumber = candidates[i];
    if (pageNumber === undefined) continue;
    const page = await params.loadPage(pageNumber);
    const chunk = findDocPackChunkContaining(
      buildDocPackReadingChunks([page], { classOverrides }),
      anchor,
    );
    if (chunk) return chunk;
    // Not the chunk's page — more candidates to scan. Warm a window in the
    // background so upcoming loads coalesce, but never await it: this resolution
    // only ever blocks on the page it needs. Start the window one past the page
    // the loop loads next (candidates[i + 1]) — that page is fetched on its own
    // by the loadPage above on the very next iteration, so warming it too would
    // just be a redundant second fetch of a page already in flight. The warm
    // therefore transfers only genuinely speculative read-ahead pages.
    const windowStart = i + 2;
    if (params.warmPages && windowStart > warmedThrough) {
      const window = candidates.slice(windowStart, windowStart + batchPages);
      if (window.length > 0) {
        warmedThrough = windowStart + window.length - 1;
        void params.warmPages(window).catch(() => undefined);
      }
    }
  }
  return undefined;
}

/** The advertised pack is one this client can decode. Takes the wire DTO
 * directly — no camelCase remap in between. */
export const isDocPackVersionUsable = (
  docPack: DocPackInfo | undefined | null,
): boolean => docPack?.content_version === DOC_PACK_CONTENT_VERSION;

const docPackActiveAudioIdAtom = Atom.make<string | null>(null).pipe(
  Atom.keepAlive,
);

export function useDocPackActiveStore<T>(
  selector: (state: {
    activeAudioId: string | null;
    setActive: (audioId: string | null) => void;
  }) => T,
): T {
  const setActive = useAtomSet(docPackActiveAudioIdAtom);
  // Derived-atom routing: gated on the selected value (zustand parity).
  return useAtomValue(docPackActiveAudioIdAtom, (activeAudioId) =>
    selector({ activeAudioId, setActive }),
  );
}
